import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, MapPin } from 'lucide-react';

// Continent → sub-region → countries (with ISO codes for flags via flagcdn.com)
export const CONTINENTS = [
  {
    name: 'Americas',
    accent: 'orange',
    subs: [
      { label: 'North America', countries: [
        { name: 'United States', code: 'us' },
        { name: 'Canada', code: 'ca' },
        { name: 'Mexico', code: 'mx' }
      ]},
      { label: 'Latin America', countries: [
        { name: 'Brazil', code: 'br' },
        { name: 'Argentina', code: 'ar' },
        { name: 'Chile', code: 'cl' },
        { name: 'Colombia', code: 'co' },
        { name: 'Peru', code: 'pe' },
        { name: 'Venezuela', code: 've' }
      ]}
    ]
  },
  {
    name: 'Europe',
    accent: 'green',
    subs: [
      { label: 'Northern Europe', countries: [
        { name: 'United Kingdom', code: 'gb' },
        { name: 'Ireland', code: 'ie' },
        { name: 'Sweden', code: 'se' },
        { name: 'Norway', code: 'no' }
      ]},
      { label: 'Western Europe', countries: [
        { name: 'France', code: 'fr' },
        { name: 'Germany', code: 'de' },
        { name: 'Switzerland', code: 'ch' },
        { name: 'Netherlands', code: 'nl' },
        { name: 'Austria', code: 'at' },
        { name: 'Belgium', code: 'be' }
      ]},
      { label: 'Eastern Europe', countries: [
        { name: 'Poland', code: 'pl' },
        { name: 'Russia', code: 'ru' }
      ]},
      { label: 'Southern Europe', countries: [
        { name: 'Italy', code: 'it' },
        { name: 'Greece', code: 'gr' },
        { name: 'Spain', code: 'es' },
        { name: 'Portugal', code: 'pt' }
      ]}
    ]
  },
  {
    name: 'Asia',
    accent: 'green',
    subs: [
      { label: 'Southern Asia', countries: [
        { name: 'India', code: 'in' }
      ]},
      { label: 'East Asia', countries: [
        { name: 'China', code: 'cn' },
        { name: 'Japan', code: 'jp' }
      ]},
      { label: 'Southeast Asia', countries: [
        { name: 'Indonesia', code: 'id' },
        { name: 'Malaysia', code: 'my' },
        { name: 'Philippines', code: 'ph' },
        { name: 'Singapore', code: 'sg' },
        { name: 'Vietnam', code: 'vn' }
      ]},
      { label: 'Middle East (Western Asia)', countries: [
        { name: 'Bahrain', code: 'bh' },
        { name: 'Oman', code: 'om' },
        { name: 'Kuwait', code: 'kw' },
        { name: 'Saudi Arabia', code: 'sa' },
        { name: 'United Arab Emirates', code: 'ae' },
        { name: 'Qatar', code: 'qa' },
        { name: 'Jordan', code: 'jo' },
        { name: 'Iraq', code: 'iq' }
      ]}
    ]
  },
  {
    name: 'Oceania',
    accent: 'orange',
    subs: [
      { label: 'Australia & NZ', countries: [
        { name: 'Australia', code: 'au' },
        { name: 'New Zealand', code: 'nz' }
      ]}
    ]
  }
];

const Flag = ({ code, alt }) => (
  <img
    src={`https://flagcdn.com/24x18/${code}.png`}
    srcSet={`https://flagcdn.com/48x36/${code}.png 2x`}
    width={20}
    height={15}
    loading="lazy"
    className="rounded-sm border border-gray-900/40 shrink-0"
    alt={alt || code}
  />
);

const ContinentBar = () => {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const accentMap = {
    green: { text: 'text-green-400', dot: 'bg-gray-500', hover: 'hover:text-green-300' },
    orange: { text: 'text-orange-400', dot: 'bg-orange-500', hover: 'hover:text-orange-300' }
  };

  return (
    <div ref={ref} className="hidden lg:block bg-[#06090a] border-b border-gray-900/40 text-gray-300 text-[12px] relative z-[60]">
      <div className="max-w-[1400px] mx-auto px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-green-500">
          <MapPin className="w-3.5 h-3.5" />
          <span>Global Presence</span>
          <span className="text-gray-600 mx-1">|</span>
          <span className="text-gray-400 normal-case tracking-normal">Select your region</span>
        </div>
        <nav className="flex items-center gap-1">
          {CONTINENTS.map(c => {
            const a = accentMap[c.accent];
            const total = c.subs.reduce((s, x) => s + x.countries.length, 0);
            const isOpen = open === c.name;
            return (
              <div key={c.name} className="relative">
                <button
                  onClick={() => setOpen(isOpen ? null : c.name)}
                  onMouseEnter={() => setOpen(c.name)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-bold uppercase tracking-wider transition-colors ${isOpen ? `${a.text} bg-gray-900/30` : `text-gray-300 ${a.hover}`}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`}></span>
                  {c.name}
                  <span className="text-[9px] text-gray-500">({total})</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                  <div
                    onMouseLeave={() => setOpen(null)}
                    className={`absolute top-full right-0 mt-1 ${c.subs.length > 1 ? 'w-[640px] grid grid-cols-2 gap-4' : 'w-72'} bg-[#1a1f2e] border border-gray-900/60 rounded-md shadow-2xl shadow-black/70 p-5 z-50 animate-fadeIn`}
                  >
                    {c.subs.map(sub => (
                      <div key={sub.label}>
                        <div className={`flex items-center gap-1.5 mb-2 pb-2 border-b border-gray-900/40 ${a.text} text-[10px] font-bold uppercase tracking-wider`}>
                          <span className={`w-1 h-1 rounded-full ${a.dot}`}></span>
                          {sub.label}
                        </div>
                        <ul className="space-y-1.5">
                          {sub.countries.map(country => (
                            <li key={country.code}>
                              <a
                                href={`/coverage#${country.code}`}
                                className="flex items-center gap-2 text-[12px] text-gray-300 hover:text-green-400 transition-colors group"
                              >
                                <Flag code={country.code} alt={country.name} />
                                <span>{country.name}</span>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default ContinentBar;
